/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef } from 'react';
import * as THREE from "three";

import { const_var, params } from '../../../../../redux/reducers/reducer';
import { controls } from '../../../../../redux/reducers/common/eventReducer';

import { extrudeSettings, railModel } from '../../../../../utils/Functions';
import BaseRailModel from '../../BaseRail';
import PillarRailModel from '../../Legs';
import CommonTruss from '../CommonTruss';
import { MaterialTruss } from '../../../CustomMaterial';
import { trussDimensions } from '../../../../../helpers/building_component/truss/truss.helper';

const roofRailDistanceY = 0.7;
const hangRailDivision = 2 / 3;

const ECT = ({ trussType, normalMap, envMap, trussModelPosInfoArr, overlapTrussArr, hiddenBaseRailIdArr, meshDelModel_CSG, isFor  }) => {

    return (
        <>
            {isFor == 'center' && <BaseRailModel normalMap={normalMap} envMap={envMap} meshDelModel_CSG={meshDelModel_CSG} />}
            {isFor == 'center' && <PillarRailModel trussModelPosInfoArr={trussModelPosInfoArr} normalMap={normalMap} envMap={envMap} meshDelModel_CSG={meshDelModel_CSG} overlapTrussArr={overlapTrussArr} hiddenBaseRailIdArr={hiddenBaseRailIdArr} roofRailDistanceY={roofRailDistanceY} hangRailDivision={hangRailDivision} />}
            <TrussModel trussType={trussType} trussModelPosInfoArr={trussModelPosInfoArr} normalMap={normalMap} envMap={envMap} meshDelModel_CSG={meshDelModel_CSG} overlapTrussArr={overlapTrussArr} hiddenBaseRailIdArr={hiddenBaseRailIdArr} isFor={isFor}/>
        </>
    )
}

export default ECT

const TrussModel = ({ trussType, trussModelPosInfoArr, normalMap, envMap, overlapTrussArr, hiddenBaseRailIdArr, isFor }) => {
    const obj_centerHRail = new THREE.Object3D();
    const obj_centerVRail = new THREE.Object3D();
    const obj_shortGapVRail = new THREE.Object3D();
    const obj_underTruss = new THREE.Object3D();
    const vector = new THREE.Vector3(1, 0, 0)

    const ref_centerHRail = useRef();
    const ref_centerVRail = useRef();
    const ref_shortGapVRail = useRef();
    const ref_underTruss = useRef();

    const connectRailWidth = 0.3;
    const connectRailHeight = 0.2;
    const offset = 0.035;

    const trussDimension = trussDimensions(isFor)
    const alignPosOffset = useMemo(() => {
        if(params.p_r_s === 1 ) return const_var.roofRegularDst * 2;
        else if(params.p_r_s === 2 || params.p_r_s === 3) return 0;    
        else return 0;
    }, [params.p_r_s, trussDimension.roofPitch])
    
    const leftDoorDataArr = useMemo(() => (const_var.newDoorsArray.filter((item) => (item.userData.wallName === 'c_b_l_w' || item.userData.wallName === 'c_b_l_s_w' || item.userData.wallName === 'L_L_R_S_W'))), [const_var.newDoorsArray]) 
    const rightDoorDataArr = useMemo(() => (const_var.newDoorsArray.filter((item) => (item.userData.wallName === 'c_b_r_w' || item.userData.wallName === 'c_b_r_s_w' || item.userData.wallName === 'R_L_L_S_W'))), [const_var.newDoorsArray]) 
    const roofAlpha = Math.atan(trussDimension.roofPitch / 12);
    const roofHeight = (trussDimension.buildingWidth / 2) * (trussDimension.roofPitch / 12);
    const roofOuterRailLength = useMemo(() => (Math.sqrt(Math.pow(trussDimension.buildingWidth / 2, 2) + Math.pow(roofHeight, 2))), [trussDimension.buildingWidth, roofHeight]);
    const roofInnerRailLength = useMemo(() => (roofOuterRailLength / 3), [roofOuterRailLength])
    const bottomBaseRailLength = (trussDimension.buildingWidth - connectRailHeight / 2 - connectRailWidth / 2) * hangRailDivision;
    const roofCenterRailHeight = ((trussDimension.buildingWidth / 2) * trussDimension.roofPitch / 12) * hangRailDivision + roofRailDistanceY;


    const connectShortRailModelDataArr = useMemo(() => {
        let dataInfoArray = [];

        const distance = 2;
        const count = Math.floor((trussDimension.buildingWidth / 6) / distance)
        for (let i = 0; i < count; i++) {
            const pos_x = trussDimension.buildingWidth / 3 + distance * i;
            const pos_y = trussDimension.buildingHeight + roofHeight / 2 + trussDimension.roofPitch / 12 * (trussDimension.buildingWidth / 4 - pos_x) - roofRailDistanceY - connectRailHeight / 2 + 0.02;
            const height = roofRailDistanceY;
            
            const tempInfoObj = {
                pos_x: pos_x,
                pos_y: pos_y,
                height: height,
            }

            dataInfoArray.push(tempInfoObj);
        }

        return dataInfoArray
    }, [trussDimension.buildingHeight, trussDimension.buildingWidth, roofHeight, trussDimension.roofPitch])

    useEffect(() => {
        if (ref_centerHRail.current && ref_centerVRail.current && ref_underTruss.current && ref_shortGapVRail.current) {
            let index_all_1 = 0;
            new Array(2).fill('').forEach((_, index_dir) => {
                let offsetForDir = 1;
                if (index_dir === 1) offsetForDir = 1;
                else offsetForDir = -1;

                new Array(trussModelPosInfoArr.length).fill('').forEach((_, index_truss) => {
                    //----- roof inner rail-----
                    obj_underTruss.position.set(offsetForDir * (-trussDimension.buildingWidth / 2 + connectRailWidth / 4), trussDimension.buildingHeight - roofRailDistanceY - connectRailHeight / 2 + 0.005 + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    obj_underTruss.rotation.y = offsetForDir * Math.PI / 2;
                    obj_underTruss.rotateOnAxis(vector, offsetForDir * roofAlpha);
                    //--------------------------

                    //----- bottom base rail-----
                    obj_centerHRail.position.set(-bottomBaseRailLength / 2, trussDimension.buildingHeight + roofHeight / 3 - roofRailDistanceY - connectRailHeight / 2 + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    obj_centerHRail.rotation.y = Math.PI / 2;
                    //---------------------

                    //----- roof center rail-----
                    obj_centerVRail.position.set(0, trussDimension.buildingHeight + roofHeight / 3 - roofRailDistanceY - connectRailHeight + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    obj_centerVRail.rotation.x = -Math.PI / 2;
                    //---------------------

                    if (hiddenBaseRailIdArr.includes(index_truss)) {
                        obj_underTruss.scale.setScalar(0);
                        obj_centerHRail.scale.setScalar(0);
                        obj_centerVRail.scale.setScalar(0);
                    } else {
                        obj_underTruss.scale.setScalar(1);
                        obj_centerHRail.scale.setScalar(1);
                        obj_centerVRail.scale.setScalar(1);
                    }
                    
                    obj_underTruss.updateMatrix();
                    ref_underTruss.current.setMatrixAt(index_all_1, obj_underTruss.matrix);
                    obj_centerVRail.updateMatrix();
                    ref_centerVRail.current.setMatrixAt(index_all_1, obj_centerVRail.matrix);
                    obj_centerHRail.updateMatrix();
                    ref_centerHRail.current.setMatrixAt(index_all_1, obj_centerHRail.matrix);
                    index_all_1 ++;
                })
            })     


            let index_all_3 = 0;
            new Array(2).fill('').forEach((_, index_dir) => {
                let offsetForDir = 1;
                if (index_dir === 0) offsetForDir = 1;
                else offsetForDir = -1;

                new Array(trussModelPosInfoArr.length).fill('').forEach((_, index_truss) => {
                    new Array(connectShortRailModelDataArr.length).fill('').forEach((_, index_rail) => {
                        obj_shortGapVRail.position.set(offsetForDir * connectShortRailModelDataArr[index_rail].pos_x, connectShortRailModelDataArr[index_rail].pos_y + alignPosOffset, trussModelPosInfoArr[index_truss]);
                        obj_shortGapVRail.rotation.x = -Math.PI / 2;

                        if (hiddenBaseRailIdArr.includes(index_truss)) {
                            obj_shortGapVRail.scale.setScalar(0);
                        } else {
                            obj_shortGapVRail.scale.setScalar(1);
                        }

                        obj_shortGapVRail.updateMatrix();
                        ref_shortGapVRail.current.setMatrixAt(index_all_3, obj_shortGapVRail.matrix);

                        index_all_3 ++;
                    })
                })
            })
            
            ref_centerHRail.current.instanceMatrix.needsUpdate = true;
            ref_centerVRail.current.instanceMatrix.needsUpdate = true;
            ref_underTruss.current.instanceMatrix.needsUpdate = true;
            ref_shortGapVRail.current.instanceMatrix.needsUpdate = true;
        }
    }, [ref_centerHRail.current, ref_centerVRail.current, ref_underTruss.current, ref_shortGapVRail.current, trussDimension.buildingWidth, trussDimension.buildingHeight, connectShortRailModelDataArr, leftDoorDataArr, rightDoorDataArr, trussModelPosInfoArr, overlapTrussArr, hiddenBaseRailIdArr, roofHeight, alignPosOffset, roofAlpha, bottomBaseRailLength, controls.isCheckActiveTab, params.activeWall])

    const connectRailModelDataArr = useMemo(() => {
        let dataInfoArray = [];

        const distance = 5;
        const count = Math.floor((bottomBaseRailLength / 2) / distance)
        for (let i = 1; i < count; i++) {
            const pos_x = distance * i;
            const verticalRailHeight = (trussDimension.roofPitch / 12 * (trussDimension.buildingWidth / 2 - pos_x) - connectRailHeight / 2) - (roofHeight / 3 - roofRailDistanceY);
            const slopeRailHeight = Math.sqrt(Math.pow(verticalRailHeight, 2) + Math.pow(distance, 2));

            const alphaDeviation = 0.97;
            const slopeRailAlpha = (Math.PI / 2 - Math.atan(verticalRailHeight / distance)) / alphaDeviation;
            
            const tempInfoObj = {
                distance: distance,
                pos_x: pos_x,
                verticalRailHeight: verticalRailHeight,
                slopeRailHeight: slopeRailHeight,
                slopeRailAlpha: slopeRailAlpha
            }

            dataInfoArray.push(tempInfoObj);
        }

        return dataInfoArray
    }, [trussDimension.buildingWidth, trussDimension.roofPitch])

    return (
        <group position={[0, -offset, 0]} rotation={[0,trussDimension.trussRotation,0]} name='ECT-truss-legs'>
            <CommonTruss trussType={trussType} trussModelPosInfoArr={trussModelPosInfoArr} overlapTrussArr={overlapTrussArr} normalMap={normalMap} envMap={envMap} roofRailDistanceY={roofRailDistanceY} />
           <>
                {/* --- roof unnder truss model --- */}
                <instancedMesh ref={ref_underTruss} args={[null, null, trussModelPosInfoArr.length * 2]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(connectRailWidth, connectRailHeight), extrudeSettings(roofInnerRailLength - 0.01 )]} />
                    <MaterialTruss normalMap={normalMap} envMap={envMap} />
                </instancedMesh>
                {/* ------------------------- */}

                {/* --- bottom base rail model --- */}
                <instancedMesh   instancedMesh ref={ref_centerHRail} args={[null, null, trussModelPosInfoArr.length]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(connectRailWidth, connectRailHeight), extrudeSettings(bottomBaseRailLength)]} />
                    <MaterialTruss normalMap={normalMap} envMap={envMap} />
                </instancedMesh>
                {/* ------------------------- */}

                {/* --- roof center rail model --- */}
                <instancedMesh ref={ref_centerVRail} args={[null, null, trussModelPosInfoArr.length]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(connectRailHeight, connectRailWidth), extrudeSettings(roofCenterRailHeight)]} />
                    <MaterialTruss normalMap={normalMap} envMap={envMap} />
                </instancedMesh>
                {/* ------------------------- */}

                {connectRailModelDataArr.map((_, index) =>
                    <group name='SBST-truss-rail-legs'>
                        <ConnectVRailModel
                            index={index}
                            trussModelPosInfoArr={trussModelPosInfoArr}
                            connectRailModelDataArr={connectRailModelDataArr}
                            connectRailHeight={connectRailHeight}
                            alignPosOffset={alignPosOffset}
                            hiddenBaseRailIdArr={hiddenBaseRailIdArr}
                            overlapTrussArr={overlapTrussArr}
                            connectRailWidth={connectRailWidth}
                            roofAlpha={roofAlpha}
                            normalMap={normalMap}
                            envMap={envMap}
                            isFor={isFor}
                        />
                        <ConnectSRailModel
                            index={index}
                            trussModelPosInfoArr={trussModelPosInfoArr}
                            connectRailModelDataArr={connectRailModelDataArr}
                            connectRailHeight={connectRailHeight}
                            alignPosOffset={alignPosOffset}
                            hiddenBaseRailIdArr={hiddenBaseRailIdArr}
                            overlapTrussArr={overlapTrussArr}
                            connectRailWidth={connectRailWidth}
                            roofAlpha={roofAlpha}
                            normalMap={normalMap}
                            envMap={envMap}
                            isFor={isFor}
                        />
                    </group>
                )}


                <instancedMesh ref={ref_shortGapVRail} args={[null, null, trussModelPosInfoArr.length * connectShortRailModelDataArr.length * 2]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(connectRailHeight, connectRailWidth), extrudeSettings(roofRailDistanceY)]} />
                    <MaterialTruss normalMap={normalMap} envMap={envMap} />
                </instancedMesh>
                {/* ------------------------- */}
            </>
        </group>
    )
}

const ConnectVRailModel = ({ index, trussModelPosInfoArr, connectRailModelDataArr, alignPosOffset, hiddenBaseRailIdArr, overlapTrussArr, connectRailHeight, connectRailWidth, roofAlpha, normalMap, envMap, isFor }) => {
    const obj_connectVRail = new THREE.Object3D();
    const ref_connectVRail = useRef();
    const roofRailDistanceY = 0.7;
    const trussDimension = trussDimensions(isFor)

    useEffect(() => {
        if (ref_connectVRail.current) {

            let index_all = 0;
            new Array(2).fill('').forEach((_, index_dir) => {
                let offsetForDir = 1;
                if (index_dir === 0) offsetForDir = 1;
                else offsetForDir = -1;

                new Array(trussModelPosInfoArr.length).fill('').forEach((_, index_truss) => {
                    obj_connectVRail.position.set(offsetForDir * connectRailModelDataArr[index].pos_x, trussDimension.buildingHeight + ((trussDimension.buildingWidth / 2) * (trussDimension.roofPitch / 12)) / 3 - roofRailDistanceY - connectRailHeight + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    obj_connectVRail.rotation.x = -Math.PI / 2;

                    if (hiddenBaseRailIdArr.includes(index_truss)) {
                        obj_connectVRail.scale.setScalar(0);
                    } else {
                        obj_connectVRail.scale.setScalar(1);
                    }

                    obj_connectVRail.updateMatrix();
                    ref_connectVRail.current.setMatrixAt(index_all, obj_connectVRail.matrix);
                    
                    index_all ++;
                })
            })
            
            ref_connectVRail.current.instanceMatrix.needsUpdate = true;
        }
    }, [ref_connectVRail.current, trussDimension.buildingWidth, trussDimension.buildingHeight, params.singleSlope, params.freeStandingSide, trussModelPosInfoArr, overlapTrussArr, trussDimension.buildingWidth, trussDimension.buildingHeight, roofAlpha, connectRailModelDataArr, hiddenBaseRailIdArr, alignPosOffset])
    
    return (
        <instancedMesh ref={ref_connectVRail} args={[null, null, trussModelPosInfoArr.length * 2]} frustumCulled={false}>
            <extrudeGeometry args={[railModel(connectRailHeight, connectRailWidth), extrudeSettings(connectRailModelDataArr[index].verticalRailHeight)]} />
            <MaterialTruss normalMap={normalMap} envMap={envMap} />
        </instancedMesh>
    )
}

const ConnectSRailModel = ({ index, trussModelPosInfoArr, connectRailModelDataArr, alignPosOffset, hiddenBaseRailIdArr, overlapTrussArr, connectRailHeight, connectRailWidth, roofAlpha, normalMap, envMap, isFor }) => {
    const obj_connectSRail = new THREE.Object3D();
    const ref_connectSRail = useRef();
    const roofRailDistanceY = 0.7;
    const trussDimension = trussDimensions(isFor)

    useEffect(() => {
        if (ref_connectSRail.current) {

            let index_all = 0;
            new Array(2).fill('').forEach((_, index_dir) => {
                let offsetForDir = 1;
                if (index_dir === 0) offsetForDir = 1;
                else offsetForDir = -1;

                new Array(trussModelPosInfoArr.length).fill('').forEach((_, index_truss) => {
                    obj_connectSRail.position.set(offsetForDir * (connectRailModelDataArr[index].pos_x - connectRailModelDataArr[index].distance), trussDimension.buildingHeight + ((trussDimension.buildingWidth / 2) * (trussDimension.roofPitch / 12)) / 3 - roofRailDistanceY + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    obj_connectSRail.rotation.y = offsetForDir * -Math.PI / 2;
                    obj_connectSRail.rotateOnAxis(new THREE.Vector3(1, 0, 0), offsetForDir * (-Math.PI / 2 - connectRailModelDataArr[index].slopeRailAlpha));

                    if (hiddenBaseRailIdArr.includes(index_truss)) {
                        obj_connectSRail.scale.setScalar(0);
                    } else {
                        obj_connectSRail.scale.setScalar(1);
                    }
                    obj_connectSRail.updateMatrix();
                    ref_connectSRail.current.setMatrixAt(index_all, obj_connectSRail.matrix);

                    index_all ++;
                })
            })

            ref_connectSRail.current.instanceMatrix.needsUpdate = true;
        }
    }, [ref_connectSRail.current, trussDimension.buildingWidth, trussDimension.buildingHeight, params.singleSlope, params.freeStandingSide, trussModelPosInfoArr, overlapTrussArr, trussDimension.buildingWidth, trussDimension.buildingHeight, roofAlpha, connectRailModelDataArr, hiddenBaseRailIdArr, alignPosOffset])
    
    return (
        <instancedMesh ref={ref_connectSRail} args={[null, null, trussModelPosInfoArr.length * 2]} frustumCulled={false}>
            <extrudeGeometry args={[railModel(connectRailWidth, connectRailHeight), extrudeSettings(connectRailModelDataArr[index].slopeRailHeight)]} />
            <MaterialTruss normalMap={normalMap} envMap={envMap} />
        </instancedMesh>
    )
}
